import React from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    FormControlLabel,
    Checkbox,
    Avatar,
    TextField,
    useTheme,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    FilterList as FilterListIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getAllInkColors } from './InkColorIcon';

const DeckFilters = ({
    archetypeFilter,
    setArchetypeFilter,
    categoryFilter,
    setCategoryFilter,
    viewMode,
    setViewMode,
    showFavoritesOnly,
    setShowFavoritesOnly,
    showMyDecksOnly,
    setShowMyDecksOnly,
    searchQuery,
    setSearchQuery,
    archetypes,
    currentUser
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const inkColors = getAllInkColors();

    const getInkColor = (color) => {
        const colorData = inkColors.find(c => c.value === color);
        return colorData?.color || '#666';
    };

    // Sprawdź czy są aktywne filtry
    const hasActiveFilters = archetypeFilter || categoryFilter || showFavoritesOnly || showMyDecksOnly || searchQuery;

    if (isSmallMobile) {
        // Mobile: Accordion z filtrami
        return (
            <Box sx={{ mb: 3 }}>
                <Accordion
                    defaultExpanded={hasActiveFilters}
                    sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.4)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        borderRadius: '12px !important',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                            margin: 0
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(196, 181, 253, 0.7)' }} />}
                        sx={{
                            '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                                gap: 1
                            }
                        }}
                    >
                        <FilterListIcon sx={{ color: 'rgba(196, 181, 253, 0.7)' }} />
                        <Typography sx={{ color: 'white', fontWeight: 600 }}>
                            Filtry {hasActiveFilters && `(${[
                                archetypeFilter && 'Archetyp',
                                categoryFilter && 'Kategoria',
                                showFavoritesOnly && 'Ulubione',
                                searchQuery && 'Wyszukiwanie'
                            ].filter(Boolean).length})`}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Pole wyszukiwania */}
                            <TextField
                                size="small"
                                placeholder="Wyszukaj po nazwie talii lub karty..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: 'white',
                                        '& fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7c3aed'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgba(196, 181, 253, 0.7)'
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#7c3aed'
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: 'rgba(196, 181, 253, 0.5)',
                                        opacity: 1
                                    }
                                }}
                            />

                            {/* Filtry w dwóch kolumnach */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Archetyp
                                    </InputLabel>
                                    <Select
                                        value={archetypeFilter}
                                        onChange={(e) => setArchetypeFilter(e.target.value)}
                                        label="Archetyp"
                                        sx={{
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#7c3aed'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                Wszystkie
                                            </Typography>
                                        </MenuItem>
                                        {archetypes.map((archetype) => (
                                            <MenuItem key={archetype.id} value={archetype.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                    <Typography sx={{ color: 'white', flex: 1, fontSize: '0.875rem' }}>
                                                        {archetype.name}
                                                    </Typography>
                                                    {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                            {archetype.ink_colors.map((color, index) => {
                                                                const colorData = inkColors.find(c => c.value === color);
                                                                return (
                                                                    <Avatar
                                                                        key={index}
                                                                        src={colorData?.icon}
                                                                        sx={{
                                                                            width: 14,
                                                                            height: 14,
                                                                            backgroundColor: getInkColor(color),
                                                                            border: `1px solid ${getInkColor(color)}`
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Kategoria
                                    </InputLabel>
                                    <Select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        label="Kategoria"
                                        sx={{
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#7c3aed'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                Wszystkie
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="unknown">Nieznana</MenuItem>
                                        <MenuItem value="to_test">Do przetestowania</MenuItem>
                                        <MenuItem value="rejected">Odrzucona</MenuItem>
                                        <MenuItem value="selected">Wybrana</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Opcje i widok */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {/* Opcje filtrowania - tylko dla zalogowanych użytkowników */}
                                {currentUser && (
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showFavoritesOnly}
                                                    onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                                                    sx={{
                                                        color: 'rgba(196, 181, 253, 0.7)',
                                                        '&.Mui-checked': {
                                                            color: '#7c3aed'
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                                    Tylko ulubione
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showMyDecksOnly}
                                                    onChange={(e) => setShowMyDecksOnly(e.target.checked)}
                                                    sx={{
                                                        color: 'rgba(196, 181, 253, 0.7)',
                                                        '&.Mui-checked': {
                                                            color: '#7c3aed'
                                                        }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                                    Moje talie
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                )}

                                {/* Przyciski widoku */}
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                        onClick={() => setViewMode('list')}
                                        sx={{
                                            color: viewMode === 'list' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                            }
                                        }}
                                    >
                                        <ViewListIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setViewMode('grid')}
                                        sx={{
                                            color: viewMode === 'grid' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                            }
                                        }}
                                    >
                                        <ViewModuleIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
    }

    if (isMobile) {
        // Tablet: Filtry w dwóch rzędach
        return (
            <Box sx={{ mb: 3 }}>
                {/* Pierwszy rząd - wyszukiwanie i widok */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Wyszukaj po nazwie talii lub karty..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#7c3aed'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                color: 'rgba(196, 181, 253, 0.7)'
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#7c3aed'
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(196, 181, 253, 0.5)',
                                opacity: 1
                            }
                        }}
                    />

                    {/* Przyciski widoku */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={() => setViewMode('list')}
                            sx={{
                                color: viewMode === 'list' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                }
                            }}
                        >
                            <ViewListIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => setViewMode('grid')}
                            sx={{
                                color: viewMode === 'grid' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                                '&:hover': {
                                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                }
                            }}
                        >
                            <ViewModuleIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Drugi rząd - filtry */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Filtruj archetyp
                        </InputLabel>
                        <Select
                            value={archetypeFilter}
                            onChange={(e) => setArchetypeFilter(e.target.value)}
                            label="Filtruj archetyp"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie archetypy
                                </Typography>
                            </MenuItem>
                            {archetypes.map((archetype) => (
                                <MenuItem key={archetype.id} value={archetype.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Typography sx={{ color: 'white', flex: 1 }}>
                                            {archetype.name}
                                        </Typography>
                                        {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                {archetype.ink_colors.map((color, index) => {
                                                    const colorData = inkColors.find(c => c.value === color);
                                                    return (
                                                        <Avatar
                                                            key={index}
                                                            src={colorData?.icon}
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                backgroundColor: getInkColor(color),
                                                                border: `1px solid ${getInkColor(color)}`
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Filtruj kategorię
                        </InputLabel>
                        <Select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            label="Filtruj kategorię"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie kategorie
                                </Typography>
                            </MenuItem>
                            <MenuItem value="unknown">Nieznana</MenuItem>
                            <MenuItem value="to_test">Do przetestowania</MenuItem>
                            <MenuItem value="rejected">Odrzucona</MenuItem>
                            <MenuItem value="selected">Wybrana</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Opcje filtrowania - tylko dla zalogowanych użytkowników */}
                    {currentUser && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showFavoritesOnly}
                                        onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                                        sx={{
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            '&.Mui-checked': {
                                                color: '#7c3aed'
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                        Tylko ulubione
                                    </Typography>
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showMyDecksOnly}
                                        onChange={(e) => setShowMyDecksOnly(e.target.checked)}
                                        sx={{
                                            color: 'rgba(196, 181, 253, 0.7)',
                                            '&.Mui-checked': {
                                                color: '#7c3aed'
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                        Moje talie
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    // Desktop: Oryginalny layout
    return (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Pole wyszukiwania */}
            <TextField
                size="small"
                placeholder="Wyszukaj po nazwie talii lub karty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                            borderColor: 'rgba(76, 29, 149, 0.5)'
                        },
                        '&:hover fieldset': {
                            borderColor: 'rgba(76, 29, 149, 0.8)'
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#7c3aed'
                        }
                    },
                    '& .MuiInputLabel-root': {
                        color: 'rgba(196, 181, 253, 0.7)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: '#7c3aed'
                    },
                    '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(196, 181, 253, 0.5)',
                        opacity: 1
                    }
                }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Filtruj archetyp
                </InputLabel>
                <Select
                    value={archetypeFilter}
                    onChange={(e) => setArchetypeFilter(e.target.value)}
                    label="Filtruj archetyp"
                    sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 29, 149, 0.5)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 29, 149, 0.8)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7c3aed'
                        }
                    }}
                >
                    <MenuItem value="">
                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Wszystkie archetypy
                        </Typography>
                    </MenuItem>
                    {archetypes.map((archetype) => (
                        <MenuItem key={archetype.id} value={archetype.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography sx={{ color: 'white', flex: 1 }}>
                                    {archetype.name}
                                </Typography>
                                {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.3 }}>
                                        {archetype.ink_colors.map((color, index) => {
                                            const colorData = inkColors.find(c => c.value === color);
                                            return (
                                                <Avatar
                                                    key={index}
                                                    src={colorData?.icon}
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        backgroundColor: getInkColor(color),
                                                        border: `1px solid ${getInkColor(color)}`
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                    Filtruj kategorię
                </InputLabel>
                <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Filtruj kategorię"
                    sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 29, 149, 0.5)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(76, 29, 149, 0.8)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7c3aed'
                        }
                    }}
                >
                    <MenuItem value="">
                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Wszystkie kategorie
                        </Typography>
                    </MenuItem>
                    <MenuItem value="unknown">Nieznana</MenuItem>
                    <MenuItem value="to_test">Do przetestowania</MenuItem>
                    <MenuItem value="rejected">Odrzucona</MenuItem>
                    <MenuItem value="selected">Wybrana</MenuItem>
                </Select>
            </FormControl>

            {/* Przyciski widoku */}
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <IconButton
                    onClick={() => setViewMode('list')}
                    sx={{
                        color: viewMode === 'list' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                        '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.1)'
                        }
                    }}
                >
                    <ViewListIcon />
                </IconButton>
                <IconButton
                    onClick={() => setViewMode('grid')}
                    sx={{
                        color: viewMode === 'grid' ? '#7c3aed' : 'rgba(196, 181, 253, 0.7)',
                        '&:hover': {
                            backgroundColor: 'rgba(124, 58, 237, 0.1)'
                        }
                    }}
                >
                    <ViewModuleIcon />
                </IconButton>
            </Box>

            {/* Opcje filtrowania - tylko dla zalogowanych użytkowników */}
            {currentUser && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showFavoritesOnly}
                                onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    '&.Mui-checked': {
                                        color: '#7c3aed'
                                    }
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                Tylko ulubione
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showMyDecksOnly}
                                onChange={(e) => setShowMyDecksOnly(e.target.checked)}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    '&.Mui-checked': {
                                        color: '#7c3aed'
                                    }
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.875rem' }}>
                                Moje talie
                            </Typography>
                        }
                    />
                </Box>
            )}
        </Box>
    );
};

export default DeckFilters;
